/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  ShowflixCarouselService,
  type ShowflixMoreLikeThisResponse,
  type TShowflixCarouselMoreLikeThisGetParams
} from "@tivocorp/data-domain-lib";

import type { IShowflixCarouselInteractor } from "../../interfaces/IShowflixCarouselInteractor";
import type { TNetworkOptions } from "../../types/TNetworkOptions";
import { Log } from "../../logging/Log";
import { ApplicationFeatureArea } from "../../enums";
import { TIVO_SERVICES } from "../../../config";
import { BaseInteractor } from "../base";
import { ServiceLocator } from "../base/ServiceLocator";

/**
 * Shared, atomic interactor wrapping {@link ShowflixCarouselService} for the
 * Content Detail screen's "You May Also Like" section.
 *
 * Stateless: a fresh `ShowflixCarouselService` is built per call via
 * {@link ServiceLocator}, and the {@link ApplicationFeatureArea} is fixed at
 * construction time so the `applicationFeatureArea` request header matches
 * the calling feature.
 */
export class ShowflixCarouselInteractor
  extends BaseInteractor
  implements IShowflixCarouselInteractor
{
  private static readonly TAG: string = "ShowflixCarouselInteractor";

  /**
   * @param mFeatureArea - ApplicationFeatureArea reported in the request
   *                       header for every call made by this instance.
   */
  public constructor(
    private readonly mFeatureArea: ApplicationFeatureArea = ApplicationFeatureArea.ALL
  ) {
    super();
  }

  /**
   * @inheritdoc
   */
  public async fetchMoreLikeThis(
    params: TShowflixCarouselMoreLikeThisGetParams,
    options?: TNetworkOptions
  ): Promise<ShowflixMoreLikeThisResponse> {
    return this.executeGuarded(
      "fetchMoreLikeThis",
      async (
        guardedSignal: AbortSignal
      ): Promise<ShowflixMoreLikeThisResponse> => {
        Log.d(ShowflixCarouselInteractor.TAG, "fetchMoreLikeThis() called");
        const service: ShowflixCarouselService = ServiceLocator.getInstance(
          ShowflixCarouselService,
          await this.buildServicesParams(
            TIVO_SERVICES.ShowflixCarousel,
            this.mFeatureArea
          )
        );
        return service.fetchMoreLikeThis(params, guardedSignal);
      },
      options
    );
  }
}
